// React-обёртка поля формулы MathLive (<math-field>): значение, события Enter / Esc / выход стрелкой,
// регистрация «активного поля» для палитр ленты, вставка формул любого формата из буфера (LaTeX, Word,
// MathML, линейная запись) - через распознавание detect.ts.
import { useEffect, useRef } from 'react';
import { MathfieldElement } from 'mathlive';
import { setupField, setupKeyboard } from './ml';
import { mathUI, type ActiveField } from './state';
import { anyToLatex, detectFormat } from './detect';

// ------------------------------------------------------------------------------------------------ значение без потерь
// MathLive в режиме формулы молча выбрасывает кириллицу (N_{пот} -> N_{}) и иногда «\%». Поэтому:
//  - при записи в поле кириллица оборачивается в \text{…} (индексы U_{вх}, R_{экв} видны и правятся);
//  - при чтении: если пользователь ничего не менял - возвращается ИСХОДНЫЙ LaTeX (а не пересериализованный);
//  - набранная в поле кириллица сразу становится \text{…} (см. onKey).
const CYR = /[А-Яа-яЁё]/;
const TEXT_CMDS = /^\\(?:text|textrm|textit|textbf|mathrm|operatorname|mbox|hbox|mathtext)\s*\{/;

/** Кириллица в режиме формулы -> \text{…} (внутри \text, \mathrm, \operatorname - как есть). */
export function protectCyrillic(latex: string): string {
  if (!CYR.test(latex || '')) return latex || '';
  let out = '';
  let i = 0;
  const s = latex;
  while (i < s.length) {
    const m = TEXT_CMDS.exec(s.slice(i));
    if (m) {
      // пропустить команду с аргументом целиком
      let j = i + m[0].length, depth = 1;
      while (j < s.length && depth) { if (s[j] === '\\') j++; else if (s[j] === '{') depth++; else if (s[j] === '}') depth--; j++; }
      out += s.slice(i, j);
      i = j;
      continue;
    }
    if (s[i] === '\\') {                          // команда или экранированный знак
      const c = /^\\(?:[A-Za-z]+|.)/.exec(s.slice(i));
      out += c ? c[0] : s[i];
      i += c ? c[0].length : 1;
      continue;
    }
    if (CYR.test(s[i])) {
      let j = i;
      while (j < s.length && /[А-Яа-яЁё]/.test(s[j])) j++;
      out += '\\text{' + s.slice(i, j) + '}';
      i = j;
      continue;
    }
    out += s[i++];
  }
  return out;
}

/** Соседние \text{а}\text{б} (набор по буквам) -> \text{аб}. */
export function mergeText(latex: string): string {
  let prev = '';
  let s = latex || '';
  while (prev !== s) { prev = s; s = s.replace(/\\text\{([^{}]*)\}\s*\\text\{([^{}]*)\}/g, '\\text{$1$2}'); }
  // \text{пот} -> пот: так формулу набирает студент (U_{вх}), экспорт выводит кириллицу прямым шрифтом и так,
  // а экспликация «где» и поиск символов видят один и тот же вид записи
  return s.replace(/\\text\{([А-Яа-яЁё]+)\}/g, '$1');
}

const fieldSrc = new WeakMap<MathfieldElement, { src: string; field: string }>();

/** Записать LaTeX в поле (с защитой кириллицы) и запомнить исходник. */
export function setFieldLatex(mf: MathfieldElement, latex: string, silent = false) {
  const v = protectCyrillic(latex || '');
  if (silent) mf.setValue(v, { silenceNotifications: true } as any); else mf.value = v;
  fieldSrc.set(mf, { src: latex || '', field: mf.value });
}

/** LaTeX поля: без правок - исходный; с правками - значение MathLive (соседние \text склеены), но без потерь. */
export function getFieldLatex(mf: MathfieldElement | null | undefined, fallback = ''): string {
  if (!mf) return fallback;
  const e = fieldSrc.get(mf);
  if (e && mf.value === e.field) return e.src;
  return mergeText(mf.value);
}

export interface MathFieldProps {
  value: string;
  onChange?: (latex: string) => void;
  /** Enter (без Shift) */
  onEnter?: () => void;
  onEscape?: () => void;
  /** выход стрелкой за край формулы: 'forward' | 'backward' */
  onMoveOut?: (dir: 'forward' | 'backward') => void;
  onBlur?: () => void;
  /** Backspace в пустом поле */
  onDeleteEmpty?: () => void;
  kind: ActiveField['kind'];
  autoFocus?: boolean;
  /** точка щелчка (clientX/Y) - поставить курсор туда */
  clickAt?: { x: number; y: number } | null;
  className?: string;
  testid?: string;
  /** выделить первое пустое место шаблона после фокуса */
  selectPlaceholder?: boolean;
  commit?: () => void;
  fieldRef?: (mf: MathfieldElement | null) => void;
  /** вставка распознана (для подсказки «Распознано: …») */
  onPasteFormat?: (name: string) => void;
}

export function MathField(p: MathFieldProps) {
  const ref = useRef<MathfieldElement | null>(null);
  const pr = useRef(p);
  pr.current = p;

  useEffect(() => {
    const mf = ref.current;
    if (!mf) return;
    setupField(mf);
    setupKeyboard();
    setFieldLatex(mf, p.value || '');
    p.fieldRef?.(mf);
    const onInput = () => pr.current.onChange?.(getFieldLatex(mf));
    const onKey = (e: KeyboardEvent) => {
      const P = pr.current;
      // Ctrl+Enter в формуле - «Готово» (а не «разрыв страницы» основного редактора)
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault(); e.stopPropagation();
        if (P.onEnter) P.onEnter();
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey && !e.altKey && P.onEnter) { e.preventDefault(); e.stopPropagation(); P.onEnter(); return; }
      // кириллица в режиме формулы (MathLive её отбрасывает) -> текстовый фрагмент \text{…}
      if (e.key.length === 1 && CYR.test(e.key) && !e.ctrlKey && !e.altKey && !e.metaKey && (mf as any).mode !== 'text') {
        e.preventDefault(); e.stopPropagation();
        mf.executeCommand(['insert', '\\text{' + e.key + '}', { format: 'latex', selectionMode: 'after' } as any]);
        pr.current.onChange?.(getFieldLatex(mf));
        return;
      }
      if (e.key === 'Escape' && P.onEscape) { e.preventDefault(); e.stopPropagation(); P.onEscape(); return; }
      if (e.key === 'Backspace' && !mf.value && P.onDeleteEmpty) { e.preventDefault(); e.stopPropagation(); P.onDeleteEmpty(); }
    };
    const onMoveOut = (e: Event) => {
      const d = (e as CustomEvent).detail?.direction;
      if (d === 'forward' || d === 'backward') { e.preventDefault(); pr.current.onMoveOut?.(d); }
    };
    const onFocus = () => mathUI.setActive({ mf, kind: pr.current.kind, commit: pr.current.commit });
    const onBlur = () => {
      // клик по экранной клавиатуре MathLive фокус не забирает; фокус ушёл - правка закончена
      setTimeout(() => {
        if (document.activeElement === mf) return;
        mathUI.clearActive(mf);
        pr.current.onBlur?.();
      }, 0);
    };
    // вставка: любой формат формулы -> LaTeX (MathLive сам понимает только LaTeX и MathJSON)
    const onPaste = (e: ClipboardEvent) => {
      const dt = e.clipboardData;
      if (!dt) return;
      const html = dt.getData('text/html');
      const text = dt.getData('text/plain');
      const src = /msEquation|<m:oMath|<math[\s>]/i.test(html) ? html : text;
      if (!src.trim()) return;
      const f = detectFormat(src);
      if (f === 'latex' && src === text && !/^\s*(\$|\\\[|\\\(|\\begin\{equation)/.test(text)) return;   // обычный LaTeX - MathLive сам
      const r = anyToLatex(src, f === 'text' ? 'linear' : f);
      if (!r.latex) return;
      e.preventDefault();
      e.stopPropagation();
      mf.executeCommand(['insert', protectCyrillic(r.latex), { format: 'latex', selectionMode: 'after' } as any]);
      pr.current.onChange?.(getFieldLatex(mf));
      pr.current.onPasteFormat?.(f);
    };
    mf.addEventListener('input', onInput);
    mf.addEventListener('keydown', onKey, { capture: true });
    mf.addEventListener('move-out', onMoveOut);
    mf.addEventListener('focusin', onFocus);
    mf.addEventListener('focusout', onBlur);
    mf.addEventListener('paste', onPaste, { capture: true });
    if (p.autoFocus) {
      mf.focus();                          // сразу: набор не должен уйти в текст документа
      requestAnimationFrame(() => {
        if (document.activeElement !== mf) mf.focus();
        const P = pr.current;
        if (P.clickAt) {
          try {
            const off = (mf as any).getOffsetFromPoint(P.clickAt.x, P.clickAt.y, { bias: 0 });
            if (off >= 0) mf.position = off;
          } catch { /* */ }
        } else if (P.selectPlaceholder && /placeholder/.test(mf.value)) {
          mf.position = 0;
          mf.executeCommand('moveToNextPlaceholder');
        } else {
          mf.executeCommand('moveToMathfieldEnd');
        }
      });
    }
    return () => {
      mf.removeEventListener('input', onInput);
      mf.removeEventListener('keydown', onKey, { capture: true } as any);
      mf.removeEventListener('move-out', onMoveOut);
      mf.removeEventListener('focusin', onFocus);
      mf.removeEventListener('focusout', onBlur);
      mf.removeEventListener('paste', onPaste, { capture: true } as any);
      mathUI.clearActive(mf);
      p.fieldRef?.(null);
    };
  }, []);

  // значение извне (правка LaTeX-кода) -> поле, без сброса курсора при собственном вводе
  useEffect(() => {
    const mf = ref.current;
    if (!mf || p.value === undefined) return;
    const cur = getFieldLatex(mf);
    if (cur === p.value || sameLatex(cur, p.value)) return;
    setFieldLatex(mf, p.value || '', document.activeElement === mf);
  }, [p.value]);

  return <math-field ref={ref as any} class={'math-field ' + (p.className || '')} data-testid={p.testid} />;
}

function sameLatex(a: string, b: string) { return a.replace(/\s+/g, '') === b.replace(/\s+/g, ''); }

export { MathfieldElement };

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX { interface IntrinsicElements { 'math-field': any } }
}
declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX { interface IntrinsicElements { 'math-field': any } }
}
